import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(value: number, currency = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
  }).format(value)
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('pt-BR').format(value)
}

// Backend serializes naive UTC datetimes (no "Z" / no offset). Browsers parse
// those as local time, shifting by the local TZ offset. Append "Z" so the
// timestamp is anchored to UTC; display logic then converts to the desired
// zone (default America/Sao_Paulo).
export function parseDate(input: string | Date): Date {
  if (input instanceof Date) return input
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(input)) {
    return new Date(input + 'Z')
  }
  return new Date(input)
}

export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'America/Sao_Paulo',
  }
  return new Intl.DateTimeFormat('pt-BR', options ?? defaultOptions).format(parseDate(date))
}

export function formatDateTime(date: string | Date): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  }).format(parseDate(date))
}

export function formatRelativeTime(date: string | Date): string {
  const now = new Date()
  const then = parseDate(date)
  const diffMs = now.getTime() - then.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'agora'
  if (diffMins < 60) return `${diffMins}min`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays < 7) return `${diffDays}d`
  return formatDate(date)
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str
  return str.slice(0, length) + '...'
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function generateId(): string {
  return crypto.randomUUID()
}

export function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>
  return (...args) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}

export function calculatePercentage(value: number, total: number): number {
  if (total === 0) return 0
  return Math.round((value / total) * 100)
}

export function getPriorityColor(priority: string): string {
  const colors: Record<string, string> = {
    low: 'text-gray-500 bg-gray-100',
    medium: 'text-blue-500 bg-blue-100',
    high: 'text-amber-500 bg-amber-100',
    critical: 'text-red-500 bg-red-100',
  }
  return colors[priority] ?? colors.medium
}

export function getStageColor(stage: string): string {
  const colors: Record<string, string> = {
    mql: '#8b5cf6',
    sql: '#3b82f6',
    qualified: '#10b981',
    proposal: '#f59e0b',
    negotiation: '#f97316',
    closing: '#ef4444',
    won: '#22c55e',
    lost: '#6b7280',
  }
  return colors[stage] ?? '#6b7280'
}
